import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  ClockIcon,
  ChartBarIcon,
  FlagIcon,
  ArrowRightOnRectangleIcon,
  TrashIcon,
  SparklesIcon,
  Cog6ToothIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '@/config/supabase';

import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { isAdmin } from '@/utils/adminEmails';
import { ShieldCheckIcon, SparklesIcon as SparklesOutline } from '@heroicons/react/24/outline';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import { UserAvatar } from '@/components/ui/user-avatar';
import { Modal } from '@/components/ui/modal';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { CommandLineIcon } from '@heroicons/react/24/outline';
import { ProductivityChat } from '../chat/ProductivityChat';
import { UsageAlert } from '../pricing/UsageAlert';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { plan, isPro, isPlusOrHigher } = useSubscription();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Successfully signed out');
      // Clerk will automatically redirect to afterSignOutUrl, but we'll also navigate as backup
      setTimeout(() => {
        navigate('/signin');
      }, 100);
    } catch (error) {
      toast.error('Failed to sign out');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) {
      toast.error('No user found');
      return;
    }
    
    setIsDeleting(true);
    try {
      // Delete user data from Supabase tables
      const { error: deleteError } = await supabase.rpc('delete_user_data', {
        user_id: user.id
      });
      
      if (deleteError) {
        console.error('Error deleting user data:', deleteError);
        // Continue with account deletion even if data cleanup fails
      }

      // Delete the Clerk user account
      await user.delete();

      toast.success('Account successfully deleted');
      navigate('/signin');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast.error(`Failed to delete account: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const updateMenuPosition = () => {
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 8,
        right: window.innerWidth - rect.right - window.scrollX
      });
    }
  };

  useEffect(() => {
    if (isMenuOpen) {
      updateMenuPosition();
      const handleResize = () => updateMenuPosition();
      const handleScroll = () => updateMenuPosition();
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMenuOpen]);

  const allNavItems = [
    { href: '/timer', label: 'Timer', icon: ClockIcon, alwaysShow: true },
    { href: '/statistics', label: 'Statistics', icon: ChartBarIcon, alwaysShow: true },
    { href: '/goals', label: 'Goals', icon: FlagIcon, settingKey: 'showGoals' },
    // { href: '/community', label: 'Community', icon: UserGroupIcon, settingKey: 'showCommunity' },
    { href: '/quotes', label: 'Quotes', icon: SparklesIcon, settingKey: 'showQuotes' },
  ];

  const navItems = allNavItems.filter(item => {
    if (!item.alwaysShow && item.settingKey && !settings.navigation[item.settingKey as keyof typeof settings.navigation]) return false;
    // Gate Goals behind Plus
    if (item.href === '/goals' && !isPlusOrHigher) return false;
    return true;
  });

  // const footerItems = [
  //   { href: '/faq', label: 'FAQ', icon: QuestionMarkCircleIcon },
  //   { href: '/about', label: 'About', icon: InformationCircleIcon },
  // ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-lg border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
              PomoMax
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    "text-muted-foreground hover:text-foreground hover:bg-primary/10"
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {plan === 'free' && (
                <Link
                  to="/pricing"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full hover:opacity-90 transition-opacity"
                >
                  <SparklesOutline className="w-3.5 h-3.5" />
                  Upgrade
                </Link>
              )}
              {/* {plan !== 'free' && (
                <span className="hidden sm:flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                  {plan === 'pro' ? 'PRO' : 'PLUS'}
                </span>
              )} */}
              <div className="relative">
                <button
                  ref={menuButtonRef}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-primary/10 transition-colors"
                >
                  <UserAvatar 
                    user={{
                      id: user?.id || '',
                      name: user?.user_metadata?.name || user?.email || 'User',
                      email: user?.email || '',
                      avatar: user?.user_metadata?.avatar_url || undefined,
                    }} 
                    className="w-8 h-8"
                    fallbackClassName="bg-primary/10 text-primary font-medium"
                  />
                </button>

                {isMenuOpen && createPortal(
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-[9998]" 
                      onClick={() => setIsMenuOpen(false)}
                    />
                    {/* Menu */}
                    <div 
                      className="fixed w-56 rounded-xl bg-card border border-border shadow-lg p-1 z-[9999]"
                      style={{
                        top: menuPosition.top,
                        right: menuPosition.right,
                      }}
                    >
                      {/* Profile disabled until community is re-enabled
                      <Link
                        to={`/profile/${user?.id}`}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors whitespace-nowrap"
                      >
                        <UserCircleIcon className="h-4 w-4" />
                        Profile
                      </Link>
                      */}

                      {/* {isAdmin(user?.primaryEmailAddress?.emailAddress) && (
                        <Link
                          to="/admin"
                          onClick={() => setIsMenuOpen(false)}
                          className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors whitespace-nowrap"
                        >
                          <ShieldCheckIcon className="h-4 w-4" />
                          Admin
                        </Link>
                      )} */}

                      <Link
                        to="/settings"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors whitespace-nowrap"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        Settings
                      </Link>
                      
                      <button
                        onClick={() => {
                          setShowShortcutsHelp(true);
                          setIsMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors whitespace-nowrap"
                      >
                        <CommandLineIcon className="h-4 w-4" />
                        Keyboard Shortcuts
                      </button>
                      
                      <button
                        onClick={() => {
                          handleSignOut();
                          setIsMenuOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors whitespace-nowrap"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      

      {/* Mobile Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-card/50 backdrop-blur-lg border-t border-border md:hidden">
        <div className="flex justify-around items-center h-16">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="flex flex-col items-center gap-1 px-4 py-2 text-muted-foreground hover:text-foreground"
            >
              <item.icon className="h-5 w-5" />
              <span className="text-xs">{item.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-card/50 backdrop-blur-lg border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center">
            {/* <div className="flex items-center gap-4">
              {footerItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              ))}
            </div> */}
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} PomoMax. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Add Delete Account Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Account"
        description="Permanently delete your account and all associated data"
      >
        <div className="p-6 space-y-6">
          <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
            <h3 className="text-lg font-medium text-red-500 flex items-center gap-2">
              <TrashIcon className="h-5 w-5" />
              Warning: This action cannot be undone
            </h3>
          </div>

          <div className="space-y-4">
            <p className="text-foreground">
              Are you sure you want to delete your account? This will:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                Permanently delete all your data
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                Remove all your statistics and achievements
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                Delete all your saved tasks and goals
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                Remove you from all community activities
              </li>
            </ul>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              To confirm, please type "DELETE_ACCOUNT" below
            </p>
            <input
              type="text"
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Type 'DELETE_ACCOUNT'"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmText('');
              }}
              className="flex-1 px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              id="delete-confirm-button"
              onClick={handleDeleteAccount}
              disabled={confirmText !== 'DELETE_ACCOUNT' || isDeleting}
              className={cn(
                "flex-1 px-4 py-2 rounded-lg font-medium transition-colors",
                "bg-red-500 text-white hover:bg-red-600",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isDeleting ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>
      </Modal>

      <Dialog open={showShortcutsHelp} onOpenChange={setShowShortcutsHelp}>
        <DialogContent className="sm:max-w-[380px] p-0">
          <VisuallyHidden>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
            <DialogDescription>Keyboard shortcuts reference</DialogDescription>
          </VisuallyHidden>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 px-5 py-4">
            <div className="flex items-center gap-3">
              <CommandLineIcon className="h-5 w-5 text-white" />
              <h2 className="text-base font-semibold text-white">Keyboard Shortcuts</h2>
            </div>
          </div>

          <div className="px-5 pb-4 -mt-2">
            <div className="bg-background rounded-xl border shadow-sm px-3 py-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-1">Tasks</p>
              {[
                { label: 'Toggle Complete', keys: ['Space'] },
                { label: 'Edit Task', keys: ['⌘', 'E'] },
                { label: 'Delete Task', keys: ['⌘', '⌫'] },
              ].map(s => (
                <div key={s.label} className="flex justify-between items-center py-1.5">
                  <span className="text-sm">{s.label}</span>
                  <div className="flex items-center gap-1">
                    {s.keys.map((k, i) => (
                      <span key={i} className="flex items-center gap-1">
                        {i > 0 && <span className="text-xs text-muted-foreground">+</span>}
                        <kbd className="px-2 py-0.5 text-xs bg-muted rounded font-medium">{k}</kbd>
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 mt-3 pt-2 border-t">Timer</p>
              {[
                { label: 'Start / Pause', keys: ['S'] },
                { label: 'Reset', keys: ['R'] },
                { label: 'Focus / Short / Long', keys: ['1', '2', '3'] },
                { label: 'New Task', keys: ['N'] },
                { label: 'Close', keys: ['Esc'] },
              ].map(s => (
                <div key={s.label} className="flex justify-between items-center py-1.5">
                  <span className="text-sm">{s.label}</span>
                  <div className="flex items-center gap-1">
                    {s.keys.map((k, i) => (
                      <kbd key={i} className="px-2 py-0.5 text-xs bg-muted rounded font-medium">{k}</kbd>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground mt-3 text-center">Shortcuts disabled when typing in inputs</p>

            <div className="mt-4">
            <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowShortcutsHelp(false)}
              >
                Got it
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {settings.navigation.showAIAssistant && isPro && <ProductivityChat />}
      <UsageAlert />
    </div>
  );
};